import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { type ColumnDef } from '@tanstack/react-table';
import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/pro-light-svg-icons';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import type { Wallet } from '../models';
import type { CustomColDef } from '@shared/components/ui/AgGridTable';

const formatCurrency = (value: number, currency: string) => {
  if (Number.isNaN(value)) return '—';
  return formatCurrencyAmount(value, currency || 'EUR');
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('es-ES', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

export const normalizeCategory = (
  category?: string,
  amount?: number,
): 'payment' | 'withdrawal' | string | undefined => {
  if (!category) {
    if (typeof amount === 'number' && amount < 0) return 'withdrawal';
    if (typeof amount === 'number' && amount >= 0) return 'payment';
    return undefined;
  }

  const normalized = category.toLowerCase();
  if (normalized.includes('reti') || normalized.includes('with')) {
    return 'withdrawal';
  }

  if (normalized.includes('pago') || normalized.includes('pay')) {
    return 'payment';
  }

  return category;
};

type BuildWalletColumns = (
  t: TFunction<'walletsModule'>,
  userCurrency: string,
  onViewWallet?: (wallet: Wallet) => void,
) => WalletsColumnDef[];

const buildWalletColumns: BuildWalletColumns = (t, userCurrency, onViewWallet) => [
  {
    id: 'id',
    accessorKey: 'id' as keyof Wallet,
    header: t('table.columns.id'),
    cell: ({ row }) => <span className="text-sm">#{row.original.id}</span>,
  },
  {
    id: 'client',
    accessorKey: 'team' as keyof Wallet,
    header: t('table.columns.client'),
    cell: ({ row }) => <span className="text-sm">{row.original.team?.name ?? '—'}</span>,
  },
  {
    id: 'amount',
    accessorKey: 'amount' as keyof Wallet,
    header: t('table.columns.amount'),
    cell: ({ row }) => {
      const amount = row.original.amount ?? row.original.balance_total;
      const currency = row.original.currency || userCurrency;
      const formattedAmount = formatCurrency(amount, currency);

      return <span className="text-sm font-semibold">{formattedAmount}</span>;
    },
  },
  {
    id: 'date',
    accessorKey: 'date' as keyof Wallet,
    header: t('table.columns.date'),
    cell: ({ row }) => (
      <span className="text-sm">
        {formatDate(row.original.date || row.original.updated_at || row.original.created_at)}
      </span>
    ),
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    width: 50,
    minWidth: 50,
    maxWidth: 50,
    resizable: false,
    cell: ({ row }) => (
      <div className="flex justify-center items-center">
        <Button
          variant="ghost"
          size="sm"
          type="button"
          data-testid="view-details-button"
          title="Ver detalles"
          aria-label={t('table.columns.actions')}
          onClick={() => onViewWallet?.(row.original)}
          className="flex justify-center items-center"
        >
          <FontAwesomeIcon icon={faEye} className="w-4 h-4" />
        </Button>
      </div>
    ),
  },
];

export interface UseWalletsColumnsOptions {
  onViewWallet?: (wallet: Wallet) => void;
  selectedColumns?: string[];
}

const mapToAgGridColumns = (columns: WalletsColumnDef[]): CustomColDef[] => {
  return columns.map((column) => {
    const accessorKey =
      'accessorKey' in column && typeof column.accessorKey === 'string' ? column.accessorKey : undefined;

    const columnId = (column.id as string) || accessorKey || String((column as any).accessorKey ?? '');

    const isActionsColumn = columnId === 'actions';

    return {
      colId: columnId,
      field: accessorKey && !isActionsColumn ? accessorKey : undefined,
      headerName: typeof column.header === 'string' ? column.header : undefined,
      pinned: isActionsColumn ? 'right' : undefined,
      width: (column as any).width ?? (column.size ? column.size * 12 : undefined),
      minWidth: (column as any).minWidth,
      maxWidth: (column as any).maxWidth,
      sortable: false,
      lockPosition: isActionsColumn ? 'right' : undefined,
      suppressMovable: isActionsColumn,
      suppressAutoSize: isActionsColumn,
      suppressSizeToFit: isActionsColumn,
      valueGetter: (params: any) => {
        if (columnId === 'amount') {
          return params.data?.amount ?? params.data?.balance_total ?? 0;
        }
        if (columnId === 'date') {
          return params.data?.date || params.data?.updated_at || params.data?.created_at || '';
        }
        if (columnId === 'id') {
          return params.data?.id;
        }
        if (columnId === 'client') {
          return params.data?.team?.name ?? '';
        }
        return undefined;
      },
      cellRenderer: (params: any) => {
        if (typeof column.cell !== 'function') {
          return params.value;
        }

        return column.cell({
          row: { original: params.data },
          getValue: () => params.value,
        } as any);
      },
    } as CustomColDef;
  });
};

export interface UseWalletsColumnsResult {
  tanStackColumns: WalletsColumnDef[];
  agGridColumnDefs: CustomColDef[];
  filteredColumnDefs: CustomColDef[];
}

export const useWalletsColumns = (options?: UseWalletsColumnsOptions): UseWalletsColumnsResult => {
  const { t } = useTranslation('walletsModule');
  const currency = useCurrency();

  const tanStackColumns = useMemo<WalletsColumnDef[]>(
    () => buildWalletColumns(t, currency, options?.onViewWallet),
    [t, currency, options?.onViewWallet],
  );

  const agGridColumnDefs = useMemo(() => mapToAgGridColumns(tanStackColumns), [tanStackColumns]);

  const filteredColumnDefs = useMemo(() => {
    const selectedIds = options?.selectedColumns;
    if (!selectedIds || selectedIds.length === 0) {
      return agGridColumnDefs.filter((column) => column.colId !== 'actions');
    }

    return agGridColumnDefs.filter((column) => selectedIds.includes(String(column.colId)));
  }, [agGridColumnDefs, options?.selectedColumns]);

  return {
    tanStackColumns,
    agGridColumnDefs,
    filteredColumnDefs,
  };
};

export type WalletsColumnDef = ColumnDef<Wallet>;
